import { memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AuraLayout,
  Button,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { BodyCopy } from "../../components/BodyCopy";
import {
  StepsCarousel,
  type CarouselStep,
} from "../../components/lola/StepsCarousel";
import { useStore } from "../../store/globalState";
import { useGalleryStore } from "../../gallery/galleryStore";
import { accentInkOnSurface } from "../../utils/accentInkOnSurface";
import { APP_ROUTES, TITLE_COLOR } from "../../constants";
import {
  CASH_DEPOSIT_AMOUNT,
  CASH_DEPOSIT_CURRENCY,
} from "../../gallery/cashDepositMock";
import { formatLabeledAmount } from "../../utils";
import step3 from "../../assets/cash_deposit_how_to/step3.svg?raw";
import step4 from "../../assets/cash_deposit_how_to/step4.svg?raw";
import step5 from "../../assets/cash_deposit_how_to/step5.svg?raw";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";


const ILLUSTRATIONS = [step3, step4, step5];

/** Steps that quote the exact phrase to say at the counter. */
const SCRIPTED_STEPS = new Set(["3"]);

const STEP_KEYS = ["3", "4", "5"] as const;

/**
 * Cash-deposit "how to pay in cash" — same illustrated carousel as Lola,
 * staged here so the cash-deposit flow can adopt it. Copy is cash-deposit
 * (barcode in-app, no WhatsApp). Footer pairs the store locator with Got it.
 */
export const HowToPayInCashPage = memo(() => {
  const { t } = useTranslation("cash-deposit");
  const navigate = useNavigate();
  const { theme } = useStore();
  const partnerColors = useGalleryStore(
    (s) => s.rawTheme.colors as Record<string, string>,
  );
  const inactiveColor = partnerColors.inactived;

  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    footerColor,
  } = colorConfig;

  const cashToBring = formatLabeledAmount(
    CASH_DEPOSIT_AMOUNT,
    CASH_DEPOSIT_CURRENCY,
  );

  const steps = useMemo<CarouselStep[]>(
    () =>
      STEP_KEYS.map((key, position) => ({
        id: key,
        illustration: ILLUSTRATIONS[position],
        title: t(`howToPayInCash.steps.${key}.title`),
        body: t(`howToPayInCash.steps.${key}.body`, {
          amount: cashToBring,
        }),
        script: SCRIPTED_STEPS.has(key)
          ? t(`howToPayInCash.steps.${key}.script`)
          : undefined,
      })),
    [t, cashToBring],
  );

  /**
   * The artwork ships a purple palette; these tokens repaint it from the
   * active theme. `color-mix` keeps the tint ladder proportional whatever the
   * partner's brand color is, so the drawings never fight the chrome.
   */
  const accentSolid = accentInkOnSurface(partnerColors, background, title);

  const themeVars = {
    "--cdh-ink": accentSolid,
    "--cdh-mid": `color-mix(in srgb, ${accentSolid} 55%, ${background})`,
    "--cdh-soft": `color-mix(in srgb, ${accentSolid} 22%, ${background})`,
    "--cdh-fill": `color-mix(in srgb, ${accentSolid} 10%, ${background})`,
    "--cdh-neutral": inactiveColor ?? footerColor,
    "--cdh-neutral-strong": footerColor,
    "--cdh-white": background,
    "--cdh-title": accentSolid,
    "--cdh-body": bodyCopy,
    "--cdh-muted": footerColor,
    "--cdh-accent": accentSolid,
    "--cdh-surface": background,
    "--cdh-border": inactiveColor ?? bodyCopy,
  } as React.CSSProperties;

  return (
    <AuraLayout colorConfig={colorConfig} className="cdh-page">
      <Layout.Header style={themeVars}>
        <Navbar
          title=""
          color={bodyCopy}
          onBackClick={() => navigate(APP_ROUTES.CASH_STORES_PAGE)}
        />
      </Layout.Header>

      <Layout.Content className="cdh-section" style={themeVars}>
        <ScrollFadeIndicator color={bodyCopy} />
        <section className="cdh-hero">
          <PageTitle
            highlight={t("howToPayInCash.titleHighlight")}
            highlightColor={TITLE_COLOR}
            secudnary={t("howToPayInCash.titleSecondary")}
            secudnaryColor={TITLE_COLOR}
          />
        </section>

        <StepsCarousel
          steps={steps}
          regionLabel={t("howToPayInCash.title")}
          stepLabel={(position, total) =>
            t("howToPayInCash.stepOf", { current: position + 1, total })
          }
          goToLabel={(position) =>
            t("howToPayInCash.goToStep", { step: position + 1 })
          }
        />
      </Layout.Content>

      <Layout.Footer style={themeVars}>
        <section className="footer-section cdh-footer">
          <BodyCopy textColor={footerColor} className="cdh-store-count">
            {t("howToPayInCash.storeCount")}
          </BodyCopy>
          <Button
            type="button"
            size="large"
            variant="outline"
            className="cdh-secondary-cta"
            /* `outline` paints its ring from `background`, so the secondary CTA
               echoes the primary's gradient instead of the kit's black default. */
            background={backgroundBtn}
            color={accentSolid}
            style={{ backgroundColor: background }}
            onClick={() =>
              navigate(`${APP_ROUTES.STORES_MAP_PAGE}?from=cash-deposit`)
            }
          >
            {t("howToPayInCash.findStores")}
          </Button>
          <Button
            type="button"
            size="large"
            className="lola-return-chat"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={() => navigate(APP_ROUTES.CASH_STORES_PAGE)}
          >
            {t("howToPayInCash.done")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
